import { getApiUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Save, Loader2, Globe, Search, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SitemapPanel from "@/components/admin/SitemapPanel";

const API = getApiUrl();

// Preset pages (matches App.js routes — keep in sync)
const PRESET_PAGES = [
  { page_id: "home", label: "Home Page", path: "/" },
  { page_id: "properties", label: "Properties Page", path: "/properties" },
  { page_id: "about", label: "About Page", path: "/about" },
  { page_id: "contact", label: "Contact Page", path: "/contact" },
  { page_id: "prices", label: "Prices Page", path: "/prices" },
  { page_id: "how-it-works", label: "How It Works Page", path: "/how-it-works" },
  { page_id: "privacy", label: "Privacy Page", path: "/privacy" },
  { page_id: "terms", label: "Terms Page", path: "/terms" },
  { page_id: "join-us", label: "Join Us Page", path: "/join-us" },
  { page_id: "valuation", label: "Valuation Page", path: "/valuation" },
  { page_id: "online-valuation", label: "Online Valuation Page", path: "/online-valuation" },
  { page_id: "testimonials", label: "Testimonials Page", path: "/testimonials" },
  { page_id: "guides", label: "Guides Page", path: "/guides" },
  { page_id: "blog", label: "Blog Page", path: "/blog" },
  { page_id: "mortgage-calculator", label: "Mortgage Calculator Page", path: "/mortgage-calculator" },
  { page_id: "moving-cost-calculator", label: "Moving Cost Calculator Page", path: "/moving-cost-calculator" },
  { page_id: "our-agents", label: "Our Agents Page", path: "/our-agents" },
  { page_id: "request-callback", label: "Request Callback Page", path: "/request-callback" },
];

const PAGE_LABELS = Object.fromEntries(PRESET_PAGES.map((p) => [p.page_id, p.label]));
const PAGE_PATHS = Object.fromEntries(PRESET_PAGES.map((p) => [p.page_id, p.path]));

const ROBOTS_OPTIONS = [
  { value: "index, follow", label: "Index, Follow (Default)" },
  { value: "index, nofollow", label: "Index, No Follow" },
  { value: "noindex, follow", label: "No Index, Follow" },
  { value: "noindex, nofollow", label: "No Index, No Follow" }
];

export const SeoTab = () => {
  const [seoSettings, setSeoSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchSeoSettings();
  }, []);

  const fetchSeoSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/seo`, { withCredentials: true });
      const fetched = response.data || [];
      const fetchedMap = Object.fromEntries(fetched.map((s) => [s.page_id, s]));
      // Merge: ensure every preset page has an entry (stub empty fields if missing).
      // Order matches PRESET_PAGES; then append any non-preset entries already in DB.
      const merged = [
        ...PRESET_PAGES.map((p) => fetchedMap[p.page_id] || {
          page_id: p.page_id,
          seo_title: "",
          seo_description: "",
          seo_keywords: "",
          seo_focus_keyword: "",
          seo_robots: "index, follow",
        }),
        ...fetched.filter((s) => !PAGE_LABELS[s.page_id]),
      ];
      setSeoSettings(merged);
    } catch (error) {
      console.error("Error fetching SEO settings:", error);
      toast.error("Failed to load SEO settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pageId, field, value) => {
    setSeoSettings(prev => prev.map(setting =>
      setting.page_id === pageId
        ? { ...setting, [field]: value }
        : setting
    ));
  };

  const handleSave = async (pageId) => {
    setSaving(pageId);
    try {
      const setting = seoSettings.find(s => s.page_id === pageId);
      await axios.put(
        `${API}/admin/seo/${pageId}`,
        {
          seo_title: setting.seo_title,
          seo_description: setting.seo_description,
          seo_keywords: setting.seo_keywords,
          seo_focus_keyword: setting.seo_focus_keyword,
          seo_robots: setting.seo_robots
        },
        { withCredentials: true }
      );
      toast.success(`SEO settings for ${PAGE_LABELS[pageId] || pageId} saved!`);
    } catch (error) {
      console.error("Error saving SEO settings:", error);
      toast.error("Failed to save SEO settings");
    } finally {
      setSaving(null);
    }
  };

  const handleGenerateSitemap = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/generate-sitemap`, {}, { withCredentials: true });
      toast.success(`Sitemap generated! ${response.data.total_urls} URLs indexed.`);
    } catch (error) {
      console.error("Error generating sitemap:", error);
      toast.error("Failed to generate sitemap");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="seo-tab">
      <div>
        <h2 className="font-serif text-2xl font-medium">SEO Settings</h2>
        <p className="text-muted-foreground mt-1">Manage search engine optimization for your pages</p>
      </div>

      {/* Sitemap panel (generation + schedule) */}
      <SitemapPanel />

      {/* Page SEO Settings */}
      {seoSettings.map((setting) => (
        <div key={setting.page_id} className="bg-white border border-border overflow-hidden" data-testid={`seo-page-${setting.page_id}`}>
          {/* Page Header */}
          <div className="bg-muted/30 border-b border-border px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <h3 className="font-serif text-lg font-medium">
                {PAGE_LABELS[setting.page_id] || setting.page_id}
              </h3>
            </div>
            <Button
              onClick={() => handleSave(setting.page_id)}
              disabled={saving === setting.page_id}
              size="sm"
              className="bg-primary text-white hover:bg-primary/90 rounded-none"
            >
              {saving === setting.page_id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          {/* SEO Fields */}
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* SEO Title */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Search size={14} />
                  SEO Title (Title Tag)
                </Label>
                <Input
                  value={setting.seo_title || ""}
                  onChange={(e) => handleChange(setting.page_id, "seo_title", e.target.value)}
                  placeholder="Page title for search engines"
                  className="rounded-none"
                  data-testid={`seo-title-${setting.page_id}`}
                />
                <p className="text-xs text-muted-foreground">
                  {(setting.seo_title || "").length}/60 characters (recommended)
                </p>
              </div>

              {/* Focus Keyword */}
              <div className="space-y-2">
                <Label>Focus Keyword</Label>
                <Input
                  value={setting.seo_focus_keyword || ""}
                  onChange={(e) => handleChange(setting.page_id, "seo_focus_keyword", e.target.value)}
                  placeholder="Primary keyword for this page"
                  className="rounded-none"
                  data-testid={`seo-focus-${setting.page_id}`}
                />
              </div>
            </div>

            {/* Meta Description */}
            <div className="space-y-2">
              <Label>Meta Description</Label>
              <Textarea
                value={setting.seo_description || ""}
                onChange={(e) => handleChange(setting.page_id, "seo_description", e.target.value)}
                placeholder="Description shown in search engine results"
                className="rounded-none resize-none"
                rows={3}
                data-testid={`seo-desc-${setting.page_id}`}
              />
              <p className="text-xs text-muted-foreground">
                {(setting.seo_description || "").length}/160 characters (recommended)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Keywords */}
              <div className="space-y-2">
                <Label>Keywords</Label>
                <Input
                  value={setting.seo_keywords || ""}
                  onChange={(e) => handleChange(setting.page_id, "seo_keywords", e.target.value)}
                  placeholder="keyword1, keyword2, keyword3"
                  className="rounded-none"
                  data-testid={`seo-keywords-${setting.page_id}`}
                />
                <p className="text-xs text-muted-foreground">Comma-separated keywords</p>
              </div>

              {/* Robots */}
              <div className="space-y-2">
                <Label>Robots Settings</Label>
                <select
                  value={setting.seo_robots || "index, follow"}
                  onChange={(e) => handleChange(setting.page_id, "seo_robots", e.target.value)}
                  className="w-full h-10 px-3 border border-input bg-background text-sm rounded-none"
                  data-testid={`seo-robots-${setting.page_id}`}
                >
                  {ROBOTS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-4 p-4 bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground mb-2">Search Engine Preview:</p>
              <div className="space-y-1">
                <p className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                  {setting.seo_title || "Page Title"}
                </p>
                <p className="text-green-700 text-sm">
                  selllocalhomes.co.uk{PAGE_PATHS[setting.page_id] !== undefined ? PAGE_PATHS[setting.page_id] : `/${setting.page_id}`}
                </p>
                <p className="text-gray-600 text-sm line-clamp-2">
                  {setting.seo_description || "Meta description will appear here..."}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Property SEO Note */}
      <div className="bg-amber-50 border border-amber-200 p-4">
        <h3 className="font-medium text-amber-900">Property SEO</h3>
        <p className="text-sm text-amber-700 mt-1">
          Individual property pages have their own SEO settings. Edit these in the Properties tab when creating or editing a property.
        </p>
      </div>
    </div>
  );
};

export default SeoTab;
